import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { ApiError } from '@/services/ApiError';
import type { Place, Ride } from '@/types/models';
import { formatMoney, newIdempotencyKey } from '@/utils/format';
import { useRequestBooking, useSegmentQuote } from '../hooks/useBookings';

const MESSAGES: Partial<Record<string, string>> = {
  RIDE_FULL: 'Sorry, this ride just filled up. Try another ride.',
  RIDE_NOT_BOOKABLE: 'This ride is no longer open for booking.',
  SELF_BOOKING_NOT_ALLOWED: 'You cannot book your own ride.',
  ACCOUNT_SUSPENDED: 'This account is suspended.',
  VALIDATION_FAILED: 'That pickup and drop-off are not both on this ride, in that order.',
};

export interface BookRideFormProps {
  ride: Ride;
  /** The part of the route this passenger is travelling, when it is not the whole thing.
   * Both or neither: half a segment is not a segment. */
  segment?: { pickup: Place; drop: Place } | undefined;
}

/**
 * Seat request. The Idempotency-Key identifies one user *intent*: it is generated once
 * and reused across retries of the same request (a network timeout, a double click), and
 * regenerated only when the request itself changes. A fresh key per HTTP attempt would
 * defeat the server's duplicate protection; one key across different requests would be
 * rejected as key reuse.
 */
export function BookRideForm({ ride, segment }: BookRideFormProps) {
  const [seats, setSeats] = useState(1);
  const [message, setMessage] = useState('');
  const [key, setKey] = useState(newIdempotencyKey);
  const booking = useRequestBooking(ride.rideId);
  // Priced by the server from the ride's stored route, never worked out here: the number
  // on this screen has to be the number the booking charges.
  const quote = useSegmentQuote(ride.rideId, segment?.pickup, segment?.drop);

  useEffect(() => setKey(newIdempotencyKey()), [seats, message]);

  if (booking.isSuccess) {
    return ride.instantBooking ? (
      <Alert tone="success" title="Seat confirmed">
        This ride books instantly, so you are in. <Link className="font-medium underline" to="/bookings">View your bookings</Link>
      </Alert>
    ) : (
      <Alert tone="success" title="Seat requested">
        The driver will confirm shortly. <Link className="font-medium underline" to="/bookings">View your bookings</Link>
      </Alert>
    );
  }

  // Falls back to the full fare while the quote is in flight or if it failed. The server
  // prices the booking either way, so a stale number here can only ever be the higher one.
  const pricePerSeat = quote.data?.pricePerSeatMinor ?? ride.pricePerSeatMinor;
  const partial = segment !== undefined && quote.data !== undefined;

  const error = booking.error;
  const duplicate = error instanceof ApiError && error.code === 'DUPLICATE_BOOKING';

  return (
    <form className="space-y-4" aria-label="Request a seat" onSubmit={(e) => {
      e.preventDefault();
      booking.mutate({
        seats,
        message: message.trim(),
        ...(segment ? { pickup: segment.pickup, drop: segment.drop } : {}),
        idempotencyKey: key,
      });
    }}>
      {error && (
        <Alert tone="error">
          {duplicate
            ? <>You already have a booking on this ride. <Link className="underline" to="/bookings">View it</Link></>
            : (error instanceof ApiError && MESSAGES[error.code]) || (error as Error).message}
        </Alert>
      )}
      <Select label="Seats" value={seats} onChange={(e) => setSeats(Number(e.target.value))}>
        {Array.from({ length: Math.max(1, ride.availableSeats) }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </Select>
      <div>
        <label htmlFor="booking-message" className="mb-1.5 block text-sm font-medium text-slate-700">Message to the driver (optional)</label>
        <textarea id="booking-message" rows={2} maxLength={300} value={message} onChange={(e) => setMessage(e.target.value)}
          className="w-full rounded-xl px-3 py-2 ring-1 ring-slate-300 focus:ring-2 focus:ring-brand-600" />
      </div>
      {partial && (
        <p className="rounded-xl bg-brand-50 px-3 py-2 text-sm text-brand-900">
          You are riding {segment?.pickup.shortName} to {segment?.drop.shortName} &mdash; about{' '}
          {Math.round(quote.data?.segmentDistanceKm ?? 0)} km of the driver's {ride.estimatedDistanceKm} km, priced
          for that part.
        </p>
      )}
      <p className="flex justify-between text-sm"><span className="text-slate-600">Total</span>
        <span className="font-semibold text-slate-900">{formatMoney(pricePerSeat * seats, ride.currency)}</span></p>
      <Button type="submit" size="lg" className="w-full" loading={booking.isPending}>
        {ride.instantBooking ? 'Book' : 'Request'} {seats} seat{seats > 1 ? 's' : ''}
      </Button>
      <p className="text-xs text-slate-500">
        You pay the driver directly.{' '}
        {ride.instantBooking ? 'This ride books instantly.' : 'Your seat is held as soon as you request.'}
      </p>
    </form>
  );
}
